// @flow

import { useRef } from 'react'

import { useSessionHeartbeat } from './useSessionHeartbeat'
import { ANALYTICS_EVENT } from '../const'

import type { UseSessionHeartbeatReturn } from './useSessionHeartbeat'

const BYTES_IN_MB = 1000000

export type P2PSessionContentType = '' | 'TV' | 'RADIO' | 'CATCHUP' | 'MOVIE' | 'TV_SHOW'

export type P2PBytes = {
  http: number,
  p2p: number,
  upload: number,
}

export type P2PSnapshot = {
  peers: number,
  bytes: P2PBytes,
}

export type UseP2PSessionOptions = {|
  contentType: P2PSessionContentType,
  contentId: string,
  contentTitle: string,
  isP2PActive: boolean,
  getSnapshot: () => P2PSnapshot,
  intervalMs?: number,
|}

export type UseP2PSessionReturn = UseSessionHeartbeatReturn

export const useP2PSession = ({
  contentType,
  contentId,
  contentTitle,
  isP2PActive,
  getSnapshot,
  intervalMs,
}: UseP2PSessionOptions): UseP2PSessionReturn => {
  const getSnapshotRef = useRef(getSnapshot)
  getSnapshotRef.current = getSnapshot

  const reportedBytesRef = useRef({ http: 0, p2p: 0, upload: 0 })

  const onSessionStart = () => {
    reportedBytesRef.current = { http: 0, p2p: 0, upload: 0 }
  }

  const takeMb = (current: number, key: string): number => {
    const reported = reportedBytesRef.current

    if (current < reported[key]) {
      reported[key] = 0
    }

    const mb = Math.floor((current - reported[key]) / BYTES_IN_MB)

    reported[key] += mb * BYTES_IN_MB

    return mb
  }

  const buildExtraPayload = (): Object => {
    const { peers, bytes } = getSnapshotRef.current()

    const httpMb = takeMb(bytes.http, 'http')
    const p2pMb = takeMb(bytes.p2p, 'p2p')
    const uploadMb = takeMb(bytes.upload, 'upload')

    return {
      peers,
      http_downloaded_mb: httpMb,
      p2p_downloaded_mb: p2pMb,
      p2p_uploaded_mb: uploadMb,
      total_downloaded_mb: httpMb + p2pMb,
    }
  }

  return useSessionHeartbeat({
    eventName: ANALYTICS_EVENT.P2P_SESSION,
    contentType,
    contentId,
    contentTitle,
    isActive: isP2PActive,
    buildExtraPayload,
    onSessionStart,
    intervalMs,
  })
}

export default useP2PSession
